/**
 * ZaloAuthCompletePage.jsx
 *
 * Intermediate page for Zalo OAuth flow.
 *
 * Why this exists: Zalo's graph API blocks profile requests from non-Vietnam IPs.
 * Our server is in London, so it can't fetch user profiles. Instead:
 * 1. Backend exchanges code for access_token (works from any IP)
 * 2. Backend redirects here with the access_token
 * 3. This page (running in user's browser, Vietnam IP) fetches the profile
 * 4. This page POSTs the profile to backend, which creates the user + JWT
 * 5. Stores JWT and redirects like normal AuthCallback
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../config/region';
import { getDeviceId } from '../lib/deviceId';

export default function ZaloAuthCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Completing Zalo sign-in...');

  useEffect(() => {
    const zaloAccessToken = searchParams.get('zalo_access_token');
    const inviteToken = searchParams.get('invite_token') || '';

    if (!zaloAccessToken) {
      setStatus('Missing Zalo token. Please try again.');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }

    (async () => {
      try {
        // Step 1: Fetch profile from Zalo graph API (user's browser = Vietnam IP)
        setStatus('Fetching your Zalo profile...');
        const profileResp = await fetch(
          `https://graph.zalo.me/v2.0/me?access_token=${encodeURIComponent(zaloAccessToken)}&fields=id,name,birthday,gender,picture`
        );
        const profile = await profileResp.json();

        if (!profile.id) {
          console.error('Zalo profile fetch failed:', profile);
          setStatus('Could not fetch Zalo profile. Please try again.');
          setTimeout(() => navigate('/login?error=zalo_profile_failed', { replace: true }), 2000);
          return;
        }

        // Step 2: Send profile to backend to create user + get JWT
        setStatus('Creating your account...');
        const apiBaseUrl = getApiBaseUrl();
        const backendRoot = apiBaseUrl.replace(/\/api$/, '');

        const completeResp = await fetch(`${backendRoot}/auth/zalo/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            zaloUserId: profile.id,
            displayName: profile.name || 'Zalo User',
            pictureUrl: profile.picture?.data?.url || null,
            inviteToken: inviteToken || null,
            // EV-1245: birthday/gender come back from the fields we already request
            // above but were previously thrown away; deviceId (hl_device_id) lets
            // auth.user_providers register WHICH device completed this login for de-dup.
            birthday: profile.birthday || null,
            gender: profile.gender || null,
            deviceId: getDeviceId(),
          }),
        });

        const data = await completeResp.json();

        if (!data.success || !data.token) {
          console.error('Zalo complete auth failed:', data);
          setStatus('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login?error=zalo_auth_failed', { replace: true }), 2000);
          return;
        }

        // Step 3: Store auth data (same as AuthCallback.jsx)
        localStorage.setItem('venueapp_token', data.token);
        localStorage.setItem('venue_app_token', data.token);
        localStorage.setItem('token', data.token);
        if (data.loginMethod) localStorage.setItem('user_loginmethod', data.loginMethod);
        if (data.name) localStorage.setItem('user_name', data.name);
        if (data.email) localStorage.setItem('user_email', data.email);
        if (data.role) localStorage.setItem('user_role', data.role);
        if (data.userId) localStorage.setItem('user_id', data.userId);
        if (data.profile_image) localStorage.setItem('user_profile_image', data.profile_image);
        if (data.locale) localStorage.setItem('hl_locale', data.locale);

        // Step 4: Determine redirect
        const pendingInvite = inviteToken
          || localStorage.getItem('pending_invite_token')
          || sessionStorage.getItem('pending_invite_token');
        localStorage.removeItem('pending_invite_token');
        sessionStorage.removeItem('pending_invite_token');

        setStatus('Success! Redirecting...');

        if (data.complete_profile === '1') {
          if (pendingInvite) sessionStorage.setItem('pending_invite_token', pendingInvite);
          window.location.href = '/complete-profile';
        } else if (pendingInvite) {
          window.location.href = '/join?token=' + encodeURIComponent(pendingInvite);
        } else if (data.is_venue_staff) {
          const dashPath = data.venue_slug ? `/venue-dashboard/${data.venue_slug}` : '/venue-dashboard';
          window.location.href = dashPath;
        } else {
          window.location.href = '/';
        }
      } catch (err) {
        console.error('Zalo auth complete error:', err);
        setStatus('Something went wrong. Please try again.');
        setTimeout(() => navigate('/login?error=zalo_auth_error', { replace: true }), 2000);
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0068FF 0%, #0052cc 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    }}>
      <div style={{ textAlign: 'center', color: 'white' }}>
        <div style={{
          width: 48, height: 48,
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px',
        }} />
        <div style={{ fontSize: 18, fontWeight: 600 }}>{status}</div>
      </div>
      <style>{`@keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
